import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import MovieCard from '@/components/base/MovieCard';
import { movies, articles } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const numericId = Number(id);

  const movie = movies.find((m) => m.id === numericId);
  const article = articles.find((a) => a.id === numericId);

  const title = movie ? movie.title : article?.title || '内容未找到';

  const relatedMovies = useMemo(() => {
    if (movie) {
      return movies
        .filter((m) => m.category === movie.category && m.id !== movie.id)
        .slice(0, 4);
    }
    if (article) {
      return movies.filter((m) => m.featured).slice(0, 4);
    }
    return [];
  }, [movie, article]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = movie || article
      ? `${title} - 555电影网高清免费电影在线观看`
      : '内容未找到 - 555电影网';
  }, [id, title, movie, article]);

  if (!movie && !article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
            <i className="ri-emotion-sad-line text-2xl text-secondary-500"></i>
          </div>
          <h1 className="text-xl font-bold font-heading text-foreground-900 mb-2">内容未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">很抱歉，您要查看的内容不存在或已被移除</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap transition-all duration-300 no-underline cursor-pointer">
            <i className="ri-home-4-line"></i> 返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const isMovie = !!movie;
  const publishDate = movie ? `${movie.year}年` : article!.publishDate;
  const category = movie ? movie.category : article!.category;
  const coverImage = movie ? movie.poster : article!.coverImage;
  const tags = movie ? movie.tags : article!.tags;

  const breadcrumbItems = [
    { name: isMovie ? '电影' : '文章', path: '/category/all' },
    { name: title },
  ];

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://555dy.com/' },
      { '@type': 'ListItem', position: 2, name: isMovie ? '电影' : '文章', item: 'https://555dy.com/category/all' },
      { '@type': 'ListItem', position: 3, name: title },
    ],
  };

  const articleSchema = {
    headline: title,
    description: movie ? movie.description : article!.summary,
    image: coverImage,
    datePublished: article ? article.publishDate : `${movie!.year}-01-01`,
    author: { '@type': 'Organization', name: '555电影网' },
    publisher: { '@type': 'Organization', name: '555电影网', url: 'https://555dy.com' },
  };

  const webpageSchema = {
    name: `${title} - 555电影`,
    description: movie ? movie.description : article!.summary,
    url: `https://555dy.com/article/${id}`,
  };

  return (
    <>
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="WebPage" data={webpageSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <Breadcrumb items={breadcrumbItems} />

          <article className="px-4 md:px-6 py-6 md:py-8">
            <div className="max-w-[900px] mx-auto">
              {isMovie ? (
                <div className="flex flex-col md:flex-row gap-6 mb-8">
                  <div className="w-full md:w-[280px] flex-shrink-0">
                    <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={coverImage}
                        alt={title}
                        title={`${title} - 555电影高清免费在线观看`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                      {title}
                    </h1>
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                      {movie && (
                        <span className="flex items-center gap-1 text-sm text-primary-600 font-semibold">
                          <i className="ri-star-fill text-accent-500"></i>
                          {movie.rating} 分
                        </span>
                      )}
                      <span className="text-sm text-foreground-500">{publishDate}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                      <span className="text-sm text-foreground-500">{category}</span>
                    </div>
                    <p className="text-base text-foreground-700 leading-relaxed mb-4">
                      {movie?.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    {movie && (
                      <button className="mt-6 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full font-semibold text-sm whitespace-nowrap transition-all duration-300 cursor-pointer">
                        <i className="ri-play-circle-line mr-2"></i>立即播放
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="aspect-[16/9] rounded-lg overflow-hidden bg-background-200 mb-6">
                    <img
                      src={coverImage}
                      alt={title}
                      title={`${title} - 555电影`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                    {title}
                  </h1>
                  <div className="flex items-center gap-3 mb-6 flex-wrap">
                    <span className="text-xs bg-accent-100 text-accent-700 px-3 py-1 rounded-full font-medium">{category}</span>
                    <span className="text-sm text-foreground-500">{publishDate}</span>
                  </div>
                </>
              )}

              {article && (
                <div
                  className="prose prose-base max-w-none text-foreground-800 leading-relaxed
                    [&_h3]:text-lg [&_h3]:font-bold [&_h3]:font-heading [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3
                    [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-sm md:[&_p]:text-base
                    [&_strong]:text-foreground-900"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              )}

              {relatedMovies.length > 0 && (
                <div className="mt-10 pt-8 border-t border-background-200/70">
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">
                    {isMovie ? '同类电影推荐' : '精选电影推荐'}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {relatedMovies.map((m) => (
                      <Link key={m.id} to={`/article/${m.id}`} className="no-underline cursor-pointer" data-product-shop="">
                        <MovieCard {...m} />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}